import type { MigrateVaultShare, MigrateVaultShareEnriched } from "@/features/migrate/types/api";
import type { SupSingleVaultV3 } from "@/types";

import { useCallback, useMemo } from "react";

import useVaults from "@/hooks/api/useVaults";
import useApi from "@/hooks/api/useApi";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/31612773-908e29fb-a328-4900-9dde-448b76d2f7b8?action=share&source=copy-link&creator=26633220
 *
 * Use 0x53144559C0d4a3304e2DD9dAfBD685247429216d for testing
 */
export default function useMigrateUserVaultShares() {
  const { isLoading: vaultsIsLoading, data: rawVaults } = useVaults("fully-cached");

  const userAddress = useWeb3UserAddress();

  const enabled = useMemo(() => !!userAddress && !vaultsIsLoading && rawVaults !== undefined, [rawVaults, userAddress, vaultsIsLoading]);

  const selectFormatter = useCallback(
    (data: MigrateVaultShare[]) =>
      data
        .map(
          share =>
            ({
              ...share,
              vault: rawVaults?.find(vault => vault.id === share.vault_id) as SupSingleVaultV3,
            }) as MigrateVaultShareEnriched
        )
        .filter(share => share.vault !== undefined && !share.vault.recommend_vault_share_withdrawal),
    [rawVaults]
  );

  const { data, isLoading, isError, mutate, error } = useApi<MigrateVaultShareEnriched[], MigrateVaultShare[]>(
    {
      path: `/migrate/user-vault-shares/${userAddress}/`,
      params: {
        force_balance: true,
      },
      api: "v2",
    },
    {
      enabled,
      strategy: "cached",
      select: selectFormatter,
    }
  );

  return {
    data,
    error,
    isLoading: vaultsIsLoading || isLoading,
    isError,
    mutate,
  };
}
